import { useCallback, useEffect, useRef, useState } from 'react';

export default function useStagedFixes(storageKey, { ttlMs = 30 * 60 * 1000 } = {}) {
  const [appliedFixes, setAppliedFixes] = useState({}); 
  const [appliedTokens, setAppliedTokens] = useState([]);
  const [intentionalStaged, setIntentionalStaged] = useState({}); 

  const expiryTimerRef = useRef(null);

  const scheduleExpiryCleanup = useCallback((expiresAt) => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    const now = Date.now();
    const delay = Math.max(0, expiresAt - now);
    if (delay === 0) {
      try { localStorage.removeItem(storageKey); } catch {}
      setAppliedFixes({}); setAppliedTokens([]);
      return;
    }
    expiryTimerRef.current = setTimeout(() => {
      try { localStorage.removeItem(storageKey); } catch {}
      setAppliedFixes({}); setAppliedTokens([]);
    }, delay);
  }, [storageKey]);

  const persistStaged = useCallback((fixesObj, tokensArr, intentionalObj) => {
    const expiresAt = Date.now() + ttlMs;
    const payload = {
      v: 1,
      expiresAt,
      appliedFixes: fixesObj || {},
      appliedTokens: Array.isArray(tokensArr) ? tokensArr : [],
      intentionalStaged: intentionalObj || {},
    };
    try { localStorage.setItem(storageKey, JSON.stringify(payload)); } catch {}
    scheduleExpiryCleanup(expiresAt);
  }, [storageKey, ttlMs, scheduleExpiryCleanup]);

  const rehydrateStaged = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const data = JSON.parse(raw);
      const { expiresAt, appliedFixes: fixes = {}, appliedTokens: tokens = [], intentionalStaged: intent = {} } = data || {};
      if (!expiresAt || Date.now() > Number(expiresAt)) { localStorage.removeItem(storageKey); return; }
      setAppliedFixes(fixes || {});
      setAppliedTokens(Array.isArray(tokens) ? tokens : []);
      setIntentionalStaged(intent || {});
      scheduleExpiryCleanup(Number(expiresAt));
    } catch { try { localStorage.removeItem(storageKey); } catch {} }
  }, [storageKey, scheduleExpiryCleanup]);

  useEffect(() => {
    rehydrateStaged();
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [rehydrateStaged]);

  return {
    appliedFixes,
    setAppliedFixes,
    appliedTokens,
    setAppliedTokens,
    intentionalStaged,
    setIntentionalStaged,
    persistStaged,
    rehydrateStaged,
  };
}




